import { createContext, createElement, useContext } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { StyleXStyles } from '@stylexjs/stylex';

/**
 * Constraints a layout manager enforces on a participating child — how that
 * child sits in the parent's flex/grid context.
 *
 * Intentionally empty for now: the context transport and subtree-reset plumbing
 * ship first, and the concrete constraint properties (flex-child, grid-child,
 * sizing, …) are added alongside the layout components that provide them. See
 * `constraintsToStyles` for the single extension point.
 */
export type LayoutConstraints = Record<string, never>;

/**
 * The value transported by `LayoutConstraintsContext`: a single
 * `LayoutConstraints` object applied uniformly to every participating child, or
 * `null` to clear inherited constraints.
 *
 * TODO: add a keyed mode that targets children individually by `layoutId`.
 * Encode it with an explicit discriminator (e.g. a tagged shape) rather than
 * inferring uniform-vs-keyed from the runtime shape of constraint fields — a
 * uniform value whose fields are object-valued (e.g. a StyleX conditional like
 * `{ default, ':hover' }`) must not be mistaken for a keyed map. To be designed
 * alongside the layout components that provide constraints.
 */
export type LayoutConstraintsValue = LayoutConstraints | null;

export const LayoutConstraintsContext =
  createContext<LayoutConstraintsValue>(null);

// Converts resolved constraints into `StyleXStyles`.
//
// No-op for now: `LayoutConstraints` carries no properties yet, so even a
// resolved object produces no styles. The concrete constraint → StyleX mapping
// is added here alongside the layout components that provide constraints; until
// then `constraintStyles` is inert so components can wire it in without a later
// change to their render output.
function constraintsToStyles(
  constraints: LayoutConstraints | null
): StyleXStyles<LayoutConstraints> | undefined {
  if (!constraints) {
    return undefined;
  }

  return undefined;
}

interface UseLayoutConstraintsParams {
  /**
   * Identifies this child to a parent layout manager for keyed constraints.
   * Accepted for forward-compatibility but currently unused — only uniform
   * constraints are supported today (see the TODO on `LayoutConstraintsValue`).
   */
  layoutId?: string;
  /** The component's own `children`, returned reset for its subtree. */
  children?: ReactNode;
}

interface UseLayoutConstraintsResult {
  /**
   * Styles to spread onto the component's root **before** its own `xstyle`, so
   * `xstyle` still wins on conflicts. Inert (`undefined`) until constraints are
   * implemented.
   */
  layoutConstraintStyles: StyleXStyles<LayoutConstraints> | undefined;
  /**
   * The component's `children`, wrapped so `LayoutConstraintsContext` is reset
   * to `null` for the subtree. Render these instead of the original `children`.
   */
  layoutConstraintResetChildren: ReactNode;
}

/**
 * Lets a component participate in a parent layout manager's constraints while
 * guaranteeing the subtree reset that keeps those constraints from cascading.
 *
 * A component consumes constraints for itself (`layoutConstraintStyles`) and
 * renders the returned children — which reset the context to `null` for the
 * subtree — in a single call, so the reset cannot be forgotten. The two returned
 * values name the consume side and the reset side explicitly:
 *
 * ```tsx
 * const { layoutConstraintStyles, layoutConstraintResetChildren } =
 *   useLayoutConstraints({ layoutId, children: props.children });
 * return (
 *   <div {...stylex.props(rootStyles, layoutConstraintStyles, xstyle)}>
 *     {layoutConstraintResetChildren}
 *   </div>
 * );
 * ```
 *
 * React context reaches every descendant regardless of depth, so without the
 * reset a layout manager's constraints would cascade into every nested
 * participant, not just its intended child. Components that only need to stop
 * that cascade (and never consume constraints themselves) should use
 * `LayoutConstraintsReset` instead.
 *
 * @param params.layoutId - Reserved for keyed mode; currently unused.
 * @param params.children - The component's `children`, returned reset.
 */
export function useLayoutConstraints({
  children,
}: UseLayoutConstraintsParams): UseLayoutConstraintsResult {
  const value = useContext(LayoutConstraintsContext);
  // Only uniform constraints exist today, so the context value applies
  // directly. TODO: select this child's constraints by `layoutId` once keyed
  // mode lands (see `LayoutConstraintsValue`).
  const layoutConstraintStyles = constraintsToStyles(value);

  return {
    layoutConstraintStyles,
    layoutConstraintResetChildren: createElement(
      LayoutConstraintsContext.Provider,
      { value: null },
      children
    ),
  };
}

/**
 * Resets `LayoutConstraintsContext` to `null` for its subtree, capping a layout
 * manager's constraints at the outermost participant on each branch.
 *
 * Use this in any component that renders free-form consumer `children` but does
 * not consume constraints itself (e.g. `Dialog`, `Accordion`, `Drawer`). It is
 * the reset half of `useLayoutConstraints`, exposed on its own for containers
 * that only need to stop the cascade.
 */
export function LayoutConstraintsReset({
  children,
}: {
  children?: ReactNode;
}): ReactElement {
  return createElement(
    LayoutConstraintsContext.Provider,
    { value: null },
    children
  );
}
